import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent, KeyboardEvent } from 'react';
import type { CrosswordData, CrosswordWord, SubmittedAnswer } from '../types';

export interface RejectedSubmission {
  wordId: string;
  nonce: number;
}

interface CrosswordProps {
  crossword: CrosswordData;
  submittedAnswers: Record<string, SubmittedAnswer>;
  selectedWordId: string | null;
  rejection: RejectedSubmission | null;
  onSelectWord: (word: CrosswordWord) => void;
  onDeselectWord: () => void;
  onDraftChange: (answer: string) => void;
  onDraftsChange: (answers: Record<string, string>) => void;
  onSubmit: () => void;
}

function buildCellMap(crossword: CrosswordData, submittedAnswers: Record<string, SubmittedAnswer>) {
  const letters = new Map<string, string>();
  for (const word of crossword.words) {
    const submitted = submittedAnswers[word.id];
    if (!submitted?.is_correct) continue;
    for (let index = 0; index < word.word_positions.length; index += 1) {
      const [row, col] = word.word_positions[index];
      letters.set(`${row}:${col}`, submitted.answer[index] ?? '');
    }
  }
  return letters;
}

function cellKey(row: number, col: number): string {
  return `${row}:${col}`;
}

function wordsForCell(crossword: CrosswordData, row: number, col: number): CrosswordWord[] {
  return crossword.words.filter((word) =>
    word.word_positions.some(([wordRow, wordCol]) => wordRow === row && wordCol === col)
  );
}

function buildStartNumbers(crossword: CrosswordData): Map<string, number> {
  const starts = new Set(crossword.words.map((word) => cellKey(word.row, word.col)));
  const numbers = new Map<string, number>();
  let next = 1;
  for (let row = 0; row < crossword.height; row += 1) {
    for (let col = 0; col < crossword.width; col += 1) {
      const key = cellKey(row, col);
      if (starts.has(key)) {
        numbers.set(key, next);
        next += 1;
      }
    }
  }
  return numbers;
}

function wordDraft(word: CrosswordWord, cells: Map<string, string>): string {
  return word.word_positions.map(([row, col]) => cells.get(cellKey(row, col)) ?? '').join('');
}

export function Crossword({
  crossword,
  submittedAnswers,
  selectedWordId,
  rejection,
  onSelectWord,
  onDeselectWord,
  onDraftChange,
  onDraftsChange,
  onSubmit,
}: CrosswordProps) {
  const submittedLetters = useMemo(
    () => buildCellMap(crossword, submittedAnswers),
    [crossword, submittedAnswers]
  );
  const startNumbers = useMemo(() => buildStartNumbers(crossword), [crossword]);
  const [localLetters, setLocalLetters] = useState<Map<string, string>>(() => new Map(submittedLetters));
  const inputRefs = useRef(new Map<string, HTMLInputElement>());
  const activeCellKey = useRef<string | null>(null);
  const localLettersRef = useRef(localLetters);
  const lastClickedCellKey = useRef<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [shakingWordId, setShakingWordId] = useState<string | null>(null);
  const selectedWord = crossword.words.find((word) => word.id === selectedWordId) ?? null;

  // Some mobile browsers leave the grid's painted layout stale after an
  // orientation change (block borders misaligned until something forces a
  // repaint), because container-query-driven sizing doesn't always repaint
  // promptly on rotation. Forcing a synchronous reflow fixes it immediately.
  useEffect(() => {
    const forceRepaint = () => {
      const el = gridRef.current;
      if (!el) return;
      el.style.display = 'none';
      void el.offsetHeight;
      el.style.display = '';
    };
    const orientationQuery = window.matchMedia('(orientation: landscape)');
    window.addEventListener('orientationchange', forceRepaint);
    orientationQuery.addEventListener('change', forceRepaint);
    return () => {
      window.removeEventListener('orientationchange', forceRepaint);
      orientationQuery.removeEventListener('change', forceRepaint);
    };
  }, []);

  // Gently vibrate the rejected word's letters
  useEffect(() => {
    if (!rejection) return;
    setShakingWordId(rejection.wordId);
    const timer = window.setTimeout(() => setShakingWordId(null), 600);
    return () => window.clearTimeout(timer);
  }, [rejection]);

  useEffect(() => {
    setLocalLetters((current) => {
      const next = new Map(current);
      for (const [key, value] of submittedLetters) next.set(key, value);
      return next;
    });
  }, [submittedLetters]);

  useEffect(() => {
    localLettersRef.current = localLetters;
  }, [localLetters]);

  const draftAnswers = useMemo(
    () => Object.fromEntries(crossword.words.map((word) => [word.id, wordDraft(word, localLetters)])),
    [crossword.words, localLetters]
  );

  useEffect(() => {
    onDraftsChange(draftAnswers);
  }, [draftAnswers, onDraftsChange]);

  useEffect(() => {
    if (selectedWord) onDraftChange(wordDraft(selectedWord, localLetters));
  }, [selectedWord, localLetters, onDraftChange]);

  const selectWord = (word: CrosswordWord) => {
    onSelectWord(word);
    onDraftChange(wordDraft(word, localLetters));
  };

  const selectCellWord = (row: number, col: number): CrosswordWord | null => {
    const words = wordsForCell(crossword, row, col);
    if (words.length === 0) return null;
    if (selectedWord && words.some((word) => word.id === selectedWord.id)) return selectedWord;
    return words.find((word) => word.direction === 'across') ?? words[0];
  };

  const handleCellClick = (row: number, col: number) => {
    const words = wordsForCell(crossword, row, col);
    const key = cellKey(row, col);
    if (words.length === 0) {
      lastClickedCellKey.current = null;
      onDeselectWord();
      return;
    }
    // Tapping the *same* cell twice in a row alternates across/down. This
    // tracks the last tapped cell directly off click events rather than
    // comparing against focus state (activeCellKey), which on touch devices
    // doesn't reliably update in time relative to the click that follows it.
    const isRepeatClick = lastClickedCellKey.current === key;
    lastClickedCellKey.current = key;
    if (isRepeatClick && selectedWord && words.length > 1) {
      const crossing = words.find((word) => word.id !== selectedWord.id);
      if (crossing) {
        selectWord(crossing);
        return;
      }
    }
    const word = selectCellWord(row, col);
    if (word) selectWord(word);
  };

  const updateCell = (row: number, col: number, raw: string) => {
    const word = selectCellWord(row, col);
    if (!word) return;
    const value = raw.replace(/[^a-z]/gi, '').toUpperCase().slice(-1);
    const key = cellKey(row, col);
    const next = new Map(localLetters);
    if (value) next.set(key, value);
    else next.delete(key);
    setLocalLetters(next);
    selectWord(word);
    onDraftChange(wordDraft(word, next));

    if (value) focusNextOpenCell(word, row, col, next);
  };

  // On larger grids the board can be wider than the viewport (scrollable);
  // .focus() alone doesn't reliably bring a cell into view across browsers,
  // so scroll explicitly rather than relying on default focus behavior.
  const focusCell = (row: number, col: number) => {
    const input = inputRefs.current.get(cellKey(row, col));
    input?.focus();
    input?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const focusNextOpenCell = (word: CrosswordWord, row: number, col: number, letters = localLetters) => {
    const index = word.word_positions.findIndex(([wordRow, wordCol]) => wordRow === row && wordCol === col);
    const nextPosition = word.word_positions
      .slice(index + 1)
      .find(([nextRow, nextCol]) => !letters.get(cellKey(nextRow, nextCol)));
    if (nextPosition) focusCell(nextPosition[0], nextPosition[1]);
  };

  // Picking a word from the clue list has no cell of its own already in
  // mind, so jump to the first still-empty letter (e.g. if the first two
  // letters are filled in, land on the third) rather than always the start.
  // Grid clicks and arrow-key moves DO have a specific cell in mind -- those
  // already focus it directly, and by the time this runs activeCellKey is
  // that same cell, which is part of the newly selected word, so the guard
  // below leaves it alone instead of jumping elsewhere.
  useEffect(() => {
    if (!selectedWord) return;
    const active = activeCellKey.current;
    const activeInWord =
      active !== null && selectedWord.word_positions.some(([row, col]) => cellKey(row, col) === active);
    if (activeInWord) return;
    const letters = localLettersRef.current;
    const target =
      selectedWord.word_positions.find(([row, col]) => !letters.get(cellKey(row, col))) ??
      selectedWord.word_positions[0];
    focusCell(target[0], target[1]);
    // Intentionally keyed only on the word id: this should run once per
    // selection, not on every keystroke that changes localLetters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWordId]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, row: number, col: number) => {
    const word = selectCellWord(row, col);
    if (!word) return;
    const index = word.word_positions.findIndex(([wordRow, wordCol]) => wordRow === row && wordCol === col);
    const focusPosition = (position?: [number, number]) => {
      if (position) focusCell(position[0], position[1]);
    };

    if (/^[a-z]$/i.test(event.key) && localLetters.get(cellKey(row, col))) {
      event.preventDefault();
      const nextPosition = word.word_positions
        .slice(index + 1)
        .find(([nextRow, nextCol]) => !localLetters.get(cellKey(nextRow, nextCol)));
      if (nextPosition) updateCell(nextPosition[0], nextPosition[1], event.key);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      // Clear-then-move in one keystroke: a filled cell empties immediately
      // and hands focus to the previous cell, so repeated Backspace presses
      // erase one letter per press (like a normal text field) instead of
      // needing a second press just to move once a cell is already empty.
      if (localLetters.get(cellKey(row, col))) updateCell(row, col, '');
      focusPosition(word.word_positions[index - 1]);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusPosition([row, col + 1]);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusPosition([row, col - 1]);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusPosition([row + 1, col]);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusPosition([row - 1, col]);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  const gridStyle = {
    '--cols': crossword.width,
    '--rows': crossword.height,
    aspectRatio: `${crossword.width} / ${crossword.height}`,
    // Cells never shrink past --cell-min; once cols/rows * that exceeds the
    // container, the grid overflows and the scroll wrapper below takes over
    // rather than squeezing every board down to the same on-screen size.
    gridTemplateColumns: `repeat(${crossword.width}, minmax(var(--cell-min), 1fr))`,
    gridTemplateRows: `repeat(${crossword.height}, minmax(var(--cell-min), 1fr))`,
  } as CSSProperties;

  const handleGridBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) onDeselectWord();
  };

  return (
    <section className="crossword-shell" aria-label="Crossword grid">
      <div className="crossword-grid-scroll">
        <div className="crossword-grid" onBlur={handleGridBlur} ref={gridRef} style={gridStyle}>
        {crossword.grid.flatMap((row, rowIndex) =>
          row.map((_cell, colIndex) => {
            const cellWords = wordsForCell(crossword, rowIndex, colIndex);
            const isBlack = cellWords.length === 0;
            const isSelected = selectedWord?.word_positions.some(
              ([wordRow, wordCol]) => wordRow === rowIndex && wordCol === colIndex
            );
            const isShaking = shakingWordId !== null && cellWords.some((word) => word.id === shakingWordId);
            const key = cellKey(rowIndex, colIndex);
            if (isBlack) {
              return <div className="cell black" key={key} onClick={onDeselectWord} />;
            }
            return (
              <label
                className={`cell ${isSelected ? 'selected' : ''} ${isShaking ? 'shake' : ''}`}
                key={key}
                onClick={() => handleCellClick(rowIndex, colIndex)}
              >
                {startNumbers.has(key) && <span className="cell-number">{startNumbers.get(key)}</span>}
                <input
                  aria-label={`Row ${rowIndex + 1}, column ${colIndex + 1}`}
                  maxLength={1}
                  onChange={(event) => updateCell(rowIndex, colIndex, event.target.value)}
                  onFocus={() => {
                    activeCellKey.current = key;
                    const word = selectCellWord(rowIndex, colIndex);
                    if (word) selectWord(word);
                  }}
                  onKeyDown={(event) => handleKeyDown(event, rowIndex, colIndex)}
                  ref={(input) => {
                    if (input) inputRefs.current.set(key, input);
                    else inputRefs.current.delete(key);
                  }}
                  value={localLetters.get(key) ?? ''}
                />
              </label>
            );
          })
        )}
        </div>
      </div>

      <p className="grid-hint">
        Type letters to fill the word, press Enter to submit. Click the focused square again to
        switch between across and down.
      </p>
    </section>
  );
}
